import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, CircularProgress, Divider, useMediaQuery } from '@mui/material';
import theme from '../../util/theme/theme';
import { SlideUpTransition } from './mobile/mobile-bottom-sheet';

interface PaymentBehaviorModalProps {
    open: boolean;
    onClose: () => void;
    paymentBehavior: any;
    isLoading: boolean;
    error: boolean;
    noData?: boolean;
}

const PaymentBehaviorModal: React.FC<PaymentBehaviorModalProps> = ({
    open,
    onClose,
    paymentBehavior,
    isLoading,
    error,
    noData,
}) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={isMobile ? false : "md"}
            fullWidth
            {...(isMobile ? { TransitionComponent: SlideUpTransition } : {})}
            sx={isMobile ? { '& .MuiDialog-container': { alignItems: 'flex-end' } } : undefined}
            PaperProps={{
                sx: isMobile
                    ? { m: 0, width: '100%', maxWidth: '100%', height: '90vh', maxHeight: '90vh', borderTopLeftRadius: 16, borderTopRightRadius: 16, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }
                    : undefined
            }}
        >
            <DialogTitle sx={{ fontWeight: 700, fontSize: 20, pb: 1 }}>
                Payment Behavior:  {paymentBehavior?.clientName}
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                        <CircularProgress />
                    </Box>
                ) : noData ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                        <Typography color="textSecondary" textAlign="center">
                            No payment behavior data available for this client yet.<br />
                            Data will appear once an invoice has been paid.
                        </Typography>
                    </Box>
                ) : error ? (
                    <Typography color="error">Failed to load payment behavior data</Typography>
                ) : paymentBehavior ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap:1 }}>
                        {/* Key Metrics */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 0.5 }}>
                            <Box sx={{ p: 2, backgroundColor: 'var(--color-container-background)', borderRadius: 2 }}>
                                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', mb: 0.5 }}>Payment Rate</Typography>
                                <Typography sx={{ fontSize: 20, fontWeight: 700, color: 'var(--color-primary)' }}>
                                    {paymentBehavior?.paymentRate}%
                                </Typography>
                            </Box>
                            <Box sx={{ p: 2, backgroundColor: 'var(--color-container-background)', borderRadius: 2 }}>
                                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', mb: 0.5 }}>Payment Tendency</Typography>
                                <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'var(--color-default-text)' }}>
                                    {paymentBehavior?.paymentTendency}
                                </Typography>
                            </Box>
                            <Box sx={{ p: 2, backgroundColor: 'var(--color-container-background)', borderRadius: 2 }}>
                                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', mb: 0.5 }}>Consistency</Typography>
                                <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'var(--color-default-text)' }}>
                                    {paymentBehavior?.consistency}
                                </Typography>
                            </Box>
                            <Box sx={{ p: 2, backgroundColor: 'var(--color-container-background)', borderRadius: 2 }}>
                                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', mb: 0.5 }}>Average Days to Payment</Typography>
                                <Typography sx={{ fontSize: 20, fontWeight: 700, color: 'var(--color-default-text)' }}>
                                    {paymentBehavior?.averageDaysToPayment} days
                                </Typography>
                            </Box>
                        </Box>
                        <Divider sx={{ my: 0.5 }} />
                        <Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1.5 }}>Invoice Summary</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
                                <Box>
                                    <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', mb: 0.5 }}>Total Invoices</Typography>
                                    <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
                                        {paymentBehavior?.invoiceCount}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', mb: 0.5 }}>Paid</Typography>
                                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'var(--color-primary)' }}>
                                        {paymentBehavior?.paidInvoices}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', mb: 0.5 }}>Partially Paid</Typography>
                                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#ff9800' }}>
                                        {paymentBehavior?.partiallyPaidInvoices}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', mb: 0.5 }}>Unpaid</Typography>
                                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#f44336' }}>
                                        {paymentBehavior?.unpaidInvoices}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        {/* <Divider sx={{ my: 0.5 }} />
                        <Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1.5 }}>Payment Statistics</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 0.5 }}>
                                <Box>
                                    <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', mb: 0.5 }}>Median Days to Payment</Typography>
                                    <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                                        {paymentBehavior?.medianDaysToPayment} days
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', mb: 0.5 }}>Min Days to Payment</Typography>
                                    <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                                        {paymentBehavior?.minDaysToPayment} days
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', mb: 0.5 }}>Max Days to Payment</Typography>
                                    <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                                        {paymentBehavior?.maxDaysToPayment} days
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', mb: 0.5 }}>Standard Deviation</Typography>
                                    <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                                        {paymentBehavior?.standardDeviation}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box> */}
                        <Divider sx={{ my: 0.5 }} />
                        {paymentBehavior?.paymentBehavior && (
                            <Box>
                                <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1.5 }}>Payment Behavior Breakdown</Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(5, 1fr)' }, gap: 2 }}>
                                    {Object.entries(JSON.parse(paymentBehavior.paymentBehavior)).map(([key, value]: [string, any]) => (
                                        <Box key={key}>
                                            <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', mb: 0.5, textTransform: 'capitalize' }}>
                                                {key.replace(/([A-Z])/g, ' $1').trim()}
                                            </Typography>
                                            <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
                                                {value}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Typography color="textSecondary">No payment behavior data available</Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button 
                    onClick={onClose}
                    variant="contained" 
                    sx={{ 
                        background: 'var(--color-primary)', 
                        color: '#fff', 
                        boxShadow: 'none', 
                        borderRadius: 2, 
                        fontWeight: 600,
                        '&:hover': { background: 'var(--color-primary)' } 
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PaymentBehaviorModal;
